import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { APP_VERSION, BUILD_DATE } from "@/lib/version";
import RefreshAppButton from "@/components/RefreshAppButton";

import { LegalFooter } from "@/components/LegalFooter";

const BLUEPRINT = "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative blueprint-grid">
        <img src={BLUEPRINT} alt="Bauplan" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a2b] via-transparent to-[#0b1a2b]/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center shadow-lg">
              <HardHat className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-head font-extrabold tracking-tight">PrüfDoc Pro</span>
          </div>
          <div>
            <h2 className="text-4xl font-head font-extrabold leading-tight">Digitale<br />PrüfDokumentation</h2>
            <p className="mt-4 text-base text-sky-100/80 max-w-md">
              Baupläne markieren, Mängel erfassen, Tickets verfolgen und automatisch eine professionelle PDF-Dokumentation erzeugen.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-head font-extrabold">PrüfDoc Pro</span>
          </div>
          <h1 className="text-3xl font-head font-extrabold text-slate-900">{title}</h1>
          <p className="mt-2 text-slate-500">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <p className="mt-10 text-center text-xs text-slate-400" data-testid="app-version">
            PrüfDoc Pro · Version {APP_VERSION} · Stand {BUILD_DATE}
          </p>
          <p className="mt-1 text-center text-xs text-slate-400" data-testid="app-author">
            erstellt von D.Weber
          </p>
          <LegalFooter className="mt-3" />
        </div>
      </div>
    </div>
  );
}

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full h-11" onClick={onClick} data-testid="google-login-btn">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
      Mit Google anmelden
    </Button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: remember });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Anmelden" subtitle="Willkommen zurück zur nächsten Prüfung.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">E-Mail</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required className="mt-1.5 h-11" data-testid="login-email-input" placeholder="name@firma.de" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Passwort</Label>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Passwort vergessen?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required className="mt-1.5 h-11" data-testid="login-password-input" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" data-testid="remember-me-checkbox" />
          Angemeldet bleiben
        </label>
        <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="login-submit-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Anmelden"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" />ODER<div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton />
      <p className="mt-4 text-center text-sm text-slate-500">
        Noch kein Konto? <Link to="/registrieren" className="text-primary font-semibold hover:underline" data-testid="register-link">Neu registrieren</Link>
      </p>
      <RefreshAppButton />
      <p className="mt-1.5 text-[11px] text-slate-400 text-center">Lädt die App neu und löscht alte Zwischenspeicher und Offline-Daten.</p>
    </AuthShell>
  );
}
